import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, delay, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  getWorkspacesList,
  getWorkspacesListError,
  getWorkspacesListResponse,
  loadMoreWorkspacesList,
  loadMoreWorkspacesListResponse,
  removeWorkspace,
  removeWorkspaceError,
  removeWorkspaceItem,
  removeWorkspaceResponse,
  saveWorkspace,
  saveWorkspaceError,
  saveWorkspaceResponse,
  searchWorkspacesList,
  searchWorkspacesListResponse,
  updateWorkspace,
  updateWorkspaceError,
  updateWorkspaceResponse,
} from './workspaces.actions';
import { WorkspacesResource } from '../resources/workspaces.resource';
import { SLIDER_CLOSE_ANIMATION_DURATION } from '../../constants/animation-constants';
import { AppState } from '../../store';
import { NotifyService } from '../../common/services/notify.service';

@Injectable()
export class WorkspacesEffects {
  @Effect()
  getWorkspaces$ = this.actions$.pipe(
    ofType(getWorkspacesList),
    withLatestFrom(this.store$),
    switchMap(([{ params, isWorkspacesView }, store]) =>
      store.account.info.workspaces_feature_enabled || isWorkspacesView
        ? this.workspacesResource.query(params).pipe(
            map((workspaces) => getWorkspacesListResponse({ workspaces })),
            catchError((error) => {
              this.notify.error(this.translate.instant(`response.${error.status}.message`));
              return of(getWorkspacesListError({ err: error }));
            }),
          )
        : of(getWorkspacesListResponse({ workspaces: [] })),
    ),
  );

  @Effect()
  searchWorkspaces$ = this.actions$.pipe(
    ofType(searchWorkspacesList),
    switchMap(({ params }) =>
      this.workspacesResource.search(params).pipe(
        map((workspaces) => searchWorkspacesListResponse({ workspaces })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getWorkspacesListError({ err: error }));
        }),
      ),
    ),
  );

  @Effect()
  loadMoreWorkspaces$ = this.actions$.pipe(
    ofType(loadMoreWorkspacesList),
    switchMap(({ params }) =>
      (params.q ? this.workspacesResource.search(params) : this.workspacesResource.query(params)).pipe(
        map((workspaces) => loadMoreWorkspacesListResponse({ workspaces })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getWorkspacesListError({ err: error }));
        }),
      ),
    ),
  );

  @Effect()
  saveWorkspace$ = this.actions$.pipe(
    ofType(saveWorkspace),
    switchMap(({ workspace }) =>
      this.workspacesResource.save(workspace, { include: 'packages' } as any).pipe(
        map((workspaceResponse) => saveWorkspaceResponse({ data: workspaceResponse })),
        catchError((response) => of(saveWorkspaceError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  updateWorkspace$ = this.actions$.pipe(
    ofType(updateWorkspace),
    switchMap(({ workspace, params, workspaceId }) =>
      this.workspacesResource.update(workspaceId, workspace, { ...params, include: 'packages' } as any).pipe(
        map((workspaceResponse) => updateWorkspaceResponse({ data: workspaceResponse })),
        catchError((response) => of(updateWorkspaceError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeWorkspace$ = this.actions$.pipe(
    ofType(removeWorkspace),
    switchMap(({ workspaceId }) =>
      this.workspacesResource.remove(workspaceId).pipe(
        map((workspaceResponse) => removeWorkspaceItem({ data: workspaceResponse })),
        catchError((response) => of(removeWorkspaceError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeWorkspaceResponse$ = this.actions$.pipe(
    ofType(removeWorkspaceItem),
    delay(SLIDER_CLOSE_ANIMATION_DURATION),
    map(({ data }) => removeWorkspaceResponse({ data })),
  );

  constructor(
    private actions$: Actions,
    private workspacesResource: WorkspacesResource,
    private store$: Store<AppState>,
    private notify: NotifyService,
    private translate: TranslateService,
  ) {}
}
